import React from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Stack,
  LinearProgress,
  Chip,
  Grid2,
  Tooltip,
  IconButton,
} from "@mui/material";
import { Security, TrendingUp, CheckCircle } from "@mui/icons-material";
import DonutChartMUI from "./charts/DonutChartMUI";
import GaugeChartMUI from "./charts/GaugeChartMUI";
import CircularProgressMUI from "./charts/CircularProgressMUI";
import TrendChartMUI from "./charts/TrendChartMUI";
import ProgressBarMUI from "./charts/ProgressBarMUI";
import transformOWASPData from "./charts/transformOWASPData";
import InfoIcon from "@mui/icons-material/Info";

const tooltipTexts = {
  "Security Posture":
    "Represents the percentage of the overall security posture in the application. A lower value indicates better security hygiene and stronger defences.",
  "Business Impact":
    "Indicates how vulnerabilities can disrupt business operations or services. Lower percentages suggest minimal risk to business continuity.",
  "Technical Impact":
    "Reflects the level of technical damage vulnerabilities can cause (data loss, system downtime). A lower value signifies a more resilient system.",
  "Vulnerability Count":
    "Total number of verified (true positive) vulnerabilities found. Fewer vulnerabilities show better application security quality.",
  "Return on Investment":
    "Indicates how cost-effective your security testing is. A lower ROI is better, showing you're reducing more risk for less spend.",
  "Risk Rating (Numeric)":
    "Represents how severe the identified vulnerabilities are, based on their criticality. A lower risk rating means fewer high-impact issues, indicating stronger security",
  "Risk Rating Weighted Score":
    "A cumulative weighted score considering severity and count. Lower scores reflect reduced overall application risk.",
  "OWASP Top 10 Standards":
    "Indicates whether critical vulnerabilities align with OWASP Top 10. Helps assess coverage against industry-recognized threats.",
  "True Positive Rate":
    "Percentage of actual, real vulnerabilities correctly identified. A high TP rate indicates strong detection capabilities and minimizes the risk of undetected vulnerabilities.",
  "False Positive Rate":
    "Percentage of flagged issues that turned out to be incorrect. A high FP rate leads to unnecessary investigation effort, alert fatigue, and reduced trust in the security tool.",
  "Vulnerability Distribution by Severity":
    "Breakdown of vulnerabilities across Critical, High, Medium, and Low. Helps prioritize fixes based on severity.",
  "Vulnerability Distribution by OWASP Top 10":
    "Categorizes issues based on OWASP Top 10 risk types (e.g., Injection, Broken Auth). Helps understand common threat types in the app.",
};

// Function to get dynamic description based on risk weighted score
const getRiskWeightedDescription = (score) => {
  if (score === 0.0) {
    return "None";
  } else if (score >= 0.1 && score <= 3.9) {
    return "Low Risk";
  } else if (score >= 4.0 && score <= 6.9) {
    return "Medium Risk";
  } else if (score >= 7.0 && score <= 8.9) {
    return "High Risk";
  } else if (score >= 9.0 && score <= 10.0) {
    return "Critical Risk";
  } else {
    return "Unknown Risk";
  }
};

// Function to get dynamic description based on score
const getSecurityPostureDescription = (score) => {
  if (score === 0.0) {
    return "None";
  } else if (score >= 0.1 && score <= 3.9) {
    return "Low Posture";
  } else if (score >= 4.0 && score <= 6.9) {
    return "Medium Posture";
  } else if (score >= 7.0 && score <= 8.9) {
    return "High Posture";
  } else if (score >= 9.0 && score <= 10.0) {
    return "Critical Posture";
  } else {
    return "Unknown";
  }
};

// Function to get dynamic description based on business impact score
const getBusinessImpactDescription = (score) => {
  if (score === 0.0) {
    return "None";
  } else if (score >= 0.1 && score <= 3.9) {
    return "Low Impact";
  } else if (score >= 4.0 && score <= 6.9) {
    return "Medium Impact";
  } else if (score >= 7.0 && score <= 8.9) {
    return "High Impact";
  } else if (score >= 9.0 && score <= 10.0) {
    return "Critical Impact";
  } else {
    return "Unknown Impact";
  }
};

// Helper to turn a technical-impact score into a label
const getTechnicalImpactDescription = (score) => {
  if (score === 0.0) {
    return "None";
  } else if (score >= 0.1 && score <= 3.9) {
    return "Low Impact";
  } else if (score >= 4.0 && score <= 6.9) {
    return "Medium Impact";
  } else if (score >= 7.0 && score <= 8.9) {
    return "High Impact";
  } else if (score >= 9.0 && score <= 10.0) {
    return "Critical Impact";
  }
  return "Unknown Impact";
};

export default function SingleRunSecurityMetricsComponent({
  singleRunSecurityMetrics,
}) {
  // Transform OWASP data for progress bars
  const owaspData = transformOWASPData(singleRunSecurityMetrics.owasp_metrics);

  const vulnerabilityData = [
    { label: "Critical", value: 8, color: "#dc2626" },
    { label: "High", value: 22, color: "#ea580c" },
    { label: "Medium", value: 41, color: "#d97706" },
    { label: "Low", value: 23, color: "#16a34a" },
  ];

  const totalVulnerabilities = vulnerabilityData.reduce(
    (sum, item) => sum + item.value,
    0
  );

  const ComplianceCard = ({ title, status }) => {
    const statusColors = {
      Meets: { bg: "#f0f9ff", text: "#1e40af", border: "#bfdbfe" },
      Partial: { bg: "#fefce8", text: "#a16207", border: "#fde047" },
      "Doesn't Meet": { bg: "#fef2f2", text: "#dc2626", border: "#fecaca" },
    };

    return (
      <Box textAlign="center">
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 400,
          }}
          display="block"
          mb={1}
        >
          {title}
        </Typography>
        <Chip
          icon={<CheckCircle sx={{ fontSize: "12px !important" }} />}
          label={status}
          size="small"
          sx={{
            bgcolor: statusColors[status].bg,
            color: statusColors[status].text,
            border: `1px solid ${statusColors[status].border}`,
            fontSize: "0.75rem",
            height: 24,
            "& .MuiChip-icon": {
              fontSize: "12px",
            },
          }}
        />
      </Box>
    );
  };

  const securityPosture = {
    title: "Security Posture",
    chart: (
      <GaugeChartMUI
        value={singleRunSecurityMetrics.securityPosture}
        max={10}
        color="#dc2626"
        size={150}
      />
    ),
    value: `${singleRunSecurityMetrics.securityPosture}/10`,
    description: getSecurityPostureDescription(
      singleRunSecurityMetrics.securityPosture
    ),
  };

  const businessImpact = {
    title: "Business Impact",
    chart: (
      <GaugeChartMUI
        value={singleRunSecurityMetrics.businessImpact}
        max={10}
        color="#ea580c"
        size={150}
      />
    ),
    value: `${singleRunSecurityMetrics.businessImpact}/10`,
    description: getBusinessImpactDescription(
      singleRunSecurityMetrics.businessImpact
    ),
  };

  const technicalImpact = {
    title: "Technical Impact",
    chart: (
      <GaugeChartMUI
        value={singleRunSecurityMetrics.technicalImpact}
        max={10}
        color="#8b5cf6"
        size={150}
      />
    ),
    value: `${singleRunSecurityMetrics.technicalImpact}/10`,
    description: getTechnicalImpactDescription(
      singleRunSecurityMetrics.technicalImpact
    ),
  };

  const vulnerabilityCount = {
    title: "Vulnerability Count",
    chart: (
      <TrendChartMUI
        value={singleRunSecurityMetrics.vulnerabilitiesCount}
        trend="up"
        color="#dc2626"
        size={90}
      />
    ),
    value: singleRunSecurityMetrics.vulnerabilitiesCount,
    description: "True Positive Vulnerabilities",
  };

  // ----

  const roiMetric = {
    title: "Return on Investment",
    chart: (
      <Typography variant="h3" fontWeight="bold" color="success.main">
        {singleRunSecurityMetrics.ROI}%
      </Typography>
    ),
    description: "ROI Performance",
  };

  const riskNumeric = {
    title: "Risk Rating (Numeric)",
    chart: (
      <GaugeChartMUI
        value={singleRunSecurityMetrics.riskRating.toFixed(1)}
        max={5.5}
        color="#dc2626"
        size={150}
      />
    ),
    description: getRiskWeightedDescription(
      singleRunSecurityMetrics.riskRatingWeightedScore
    ),
  };

  const riskWeighted = {
    title: "Risk Rating Weighted Score",
    chart: (
      <GaugeChartMUI
        value={singleRunSecurityMetrics.riskRatingWeightedScore.toFixed(1)}
        max={10}
        color="#ea580c"
        size={150}
      />
    ),
    description: getRiskWeightedDescription(
      singleRunSecurityMetrics.riskRatingWeightedScore
    ),
  };

  const getComplianceStatus = () => {
    const {
      Critical = 0,
      High = 0,
      Medium = 0,
    } = singleRunSecurityMetrics.severityBreakdown;

    if (Critical >= 1 || High >= 1 || Medium >= 1) return "Doesn't Meet";
    return "Meets";
  };

  const status = getComplianceStatus();
  const description =
    status === "Meets"
      ? "All Standards Met"
      : status === "Partial"
      ? "Partially Meets Standards"
      : "Standards Not Met";

  const compliance = {
    title: "OWASP Top 10 Standards",
    chart: (
      <Grid container spacing={1} mb={2}>
        <Grid item xs={12}>
          <ComplianceCard title="OWASP" status={status} />
        </Grid>
      </Grid>
    ),
    description: description,
  };

  const truePositiveRate = {
    title: "True Positive Rate",
    chart: (
      <CircularProgressMUI
        percentage={singleRunSecurityMetrics.truePositiveRate}
        color="#3b82f6"
        size={120}
      />
    ),
    value: `${singleRunSecurityMetrics.truePositiveRate.toFixed(1)}%`,
    description: "Accurate Vulnerability Detection",
  };

  const falsePositiveRate = {
    title: "False Positive Rate",
    chart: (
      <CircularProgressMUI
        percentage={singleRunSecurityMetrics.falsePositiveRate}
        color="#16a34a"
        size={120}
      />
    ),
    value: `${singleRunSecurityMetrics.falsePositiveRate.toFixed(1)}%`,
    description: "False Alarm Rate",
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        backgroundColor: "#FFFFFF",
        minHeight: "100vh",
      }}
    >
      {/* Core Security Metrics */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 4, md: 6 } }}>
        {[
          securityPosture,
          businessImpact,
          technicalImpact,
          vulnerabilityCount,
        ].map((metric, i) => (
          <Grid key={i} item xs={12} sm={6} md={6} lg={3}>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "#5391F7",
                borderRadius: 2,
                pt: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                position: "relative", // Added for absolute positioning
              }}
            >
              {/* Info Icon - Top Right Corner */}
              <Tooltip
                title={tooltipTexts[metric.title] || ""}
                placement="top"
                arrow
              >
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    padding: "4px",
                    color: "#5391F7",
                    "&:hover": {
                      backgroundColor: "rgba(83, 145, 247, 0.1)",
                    },
                  }}
                  aria-label="Info"
                >
                  <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                </IconButton>
              </Tooltip>

              <Stack spacing={2} alignItems="center" sx={{ height: "100%" }}>
                <Typography
                  textAlign="center"
                  sx={{
                    fontSize: "18px",
                    fontWeight: 500,
                    paddingRight: 3, // Prevents title from overlapping with icon
                  }}
                >
                  {metric.title}
                </Typography>

                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  {/* Chart and Value Section */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      flex: 1,
                      justifyContent: "center",
                    }}
                  >
                    {metric.title !== "Vulnerability Count" && (
                      <Box>{metric.chart}</Box>
                    )}
                    <Typography sx={{ fontSize: "29px", fontWeight: 600 }}>
                      {metric.title === "Vulnerability Count" && metric.value}
                    </Typography>
                  </Box>

                  {/* Description Section - Fixed at bottom */}
                  <Box
                    sx={{
                      textAlign: "center",
                      minHeight: "60px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: 400,
                      }}
                    >
                      {metric.description}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Risk and ROI Metrics */}
      <Grid
        container
        spacing={{ xs: 2, sm: 3 }}
        sx={{ mt: { xs: 4, md: 6 }, mb: { xs: 3, md: 4 } }}
      >
        {[roiMetric, riskNumeric, riskWeighted, compliance].map((metric, i) => (
          <Grid key={i} item xs={12} sm={6} md={6} lg={3}>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "#5391F7",
                borderRadius: 2,
                pt: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                position: "relative", // Added for absolute positioning
              }}
            >
              <Tooltip
                title={tooltipTexts[metric.title] || ""}
                placement="top"
                arrow
              >
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    padding: "4px",
                    color: "#5391F7",
                    "&:hover": {
                      backgroundColor: "rgba(83, 145, 247, 0.1)",
                    },
                  }}
                  aria-label="Info"
                >
                  <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                </IconButton>
              </Tooltip>

              <Stack spacing={2} alignItems="center" sx={{ height: "100%" }}>
                <Typography
                  textAlign="center"
                  sx={{
                    fontSize: "18px",
                    fontWeight: 500,
                    paddingRight: 6,
                    paddingLeft: 6,
                  }}
                >
                  {metric.title}
                </Typography>

                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "space-between", // Changed from center
                    width: "100%",
                  }}
                >
                  {/* Chart Section */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      flex: 1,
                      justifyContent: "center",
                    }}
                  >
                    <Box>{metric.chart}</Box>
                  </Box>

                  {/* Description Section - Fixed at bottom */}
                  <Box
                    sx={{
                      textAlign: "center",
                      minHeight: "60px", // Fixed minimum height for descriptions
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "15px",
                        fontWeight: 400,
                        lineHeight: 1.4,
                      }}
                    >
                      {metric.description}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Accuracy Metrics */}
      <Grid
        container
        spacing={{ xs: 2, sm: 3 }}
        sx={{ mt: { xs: 4, md: 6 }, mb: { xs: 4, md: 6 } }}
      >
        {[truePositiveRate, falsePositiveRate].map((metric, i) => (
          <Grid key={i} item xs={12} md={6}>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "#5391F7",
                borderRadius: 2,
                p: 2,
                height: "100%",
                position: "relative", // Added for absolute positioning
              }}
            >
              <Tooltip
                title={tooltipTexts[metric.title] || ""}
                placement="top"
                arrow
              >
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    padding: "4px",
                    color: "#5391F7",
                    "&:hover": {
                      backgroundColor: "rgba(83, 145, 247, 0.1)",
                    },
                  }}
                  aria-label="Info"
                >
                  <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
                </IconButton>
              </Tooltip>
              <Stack spacing={2} alignItems="center" height="100%">
                <Typography
                  textAlign="center"
                  sx={{ fontSize: "21px", fontWeight: 500 }}
                >
                  {metric.title}
                </Typography>
                <Box
                  flex={1}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Box mb={3}>{metric.chart}</Box>
                  <Box textAlign="center">
                    {/* <Typography variant="h5" fontWeight="bold">
                      {metric.value}
                    </Typography> */}
                    <Typography sx={{ fontSize: "15px", fontWeight: 400 }}>
                      {metric.description}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Vulnerability Distribution */}
      <Grid
        container
        spacing={{ xs: 2, sm: 3 }}
        sx={{ mt: { xs: 4, md: 6 }, mb: { xs: 4, md: 6 } }}
      >
        <Grid item xs={12}>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "#5391F7",
              borderRadius: 2,
              p: { xs: 2, sm: 3 },
              position: "relative", // Add this to establish positioning context
            }}
          >
            <Tooltip
              title={tooltipTexts["Vulnerability Distribution by Severity"]}
              placement="top"
              arrow
            >
              <IconButton
                size="small"
                sx={{
                  position: "absolute",
                  top: 16, // Increased from 8 to give more space from border
                  right: 16, // Increased from 8 to give more space from border
                  padding: "4px",
                  color: "#5391F7",
                  "&:hover": {
                    backgroundColor: "rgba(83, 145, 247, 0.1)",
                  },
                }}
                aria-label="Info"
              >
                <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
              </IconButton>
            </Tooltip>
            <Typography sx={{ fontSize: "23px", fontWeight: 400 }}>
              Vulnerability Distribution by Severity
            </Typography>
            <Box display="flex" justifyContent="center" mt={2}>
              <DonutChartMUI
                singleRunSecurityMetrics={singleRunSecurityMetrics}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* OWASP Top 10 Distribution */}
      <Grid
        container
        spacing={{ xs: 2, sm: 3 }}
        sx={{ mt: { xs: 2 }, mb: { xs: 4, md: 6 } }}
      >
        <Grid item xs={12}>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "#5391F7",
              borderRadius: 2,
              p: { xs: 2, sm: 3 },
              height: "100%",
              position: "relative",
            }}
          >
            <Tooltip
              title={tooltipTexts["Vulnerability Distribution by OWASP Top 10"]}
              placement="top"
              arrow
            >
              <IconButton
                size="small"
                sx={{
                  position: "absolute",
                  top: 16, // Increased from 8 to give more space from border
                  right: 16, // Increased from 8 to give more space from border
                  padding: "4px",
                  color: "#5391F7",
                  "&:hover": {
                    backgroundColor: "rgba(83, 145, 247, 0.1)",
                  },
                }}
                aria-label="Info"
              >
                <InfoIcon fontSize="small" sx={{ color: "grey.500" }} />
              </IconButton>
            </Tooltip>
            <Typography sx={{ fontSize: "23px", fontWeight: 400, mb: 2 }}>
              Vulnerability Distribution by OWASP Top 10
            </Typography>

            {owaspData.length > 0 ? (
              <Stack spacing={2}>
                {owaspData.map((item, i) => (
                  <ProgressBarMUI
                    key={i}
                    label={item.label}
                    value={item.value}
                    total={totalVulnerabilities}
                    color={item.color}
                  />
                ))}
              </Stack>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                No OWASP categorization data available
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
